import { useState } from "react";
import { http, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Eye, Trash2 } from "lucide-react";

export default function GuestIdSection({ docs = [], canDelete = false, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const uploaded = docs.filter((d) => d.status === "UPLOADED");

  const viewDoc = async (id) => {
    setBusyId(id);
    try {
      const r = await http.get(`/admin/guest-ids/${id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusyId(null); }
  };

  const delDoc = async (id) => {
    if (!window.confirm("Permanently delete this ID document? The file will be destroyed and only non-sensitive audit metadata is kept. Booking, stay and payment history are not affected.")) return;
    setBusyId(id);
    try {
      await http.delete(`/admin/guest-ids/${id}`);
      toast.success("ID document permanently deleted");
      onChanged && onChanged();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusyId(null); }
  };

  return (
    <div className="card" data-testid="guest-id-section">
      <h3 className="font-serif text-2xl mb-1 flex items-center gap-2"><ShieldCheck size={20} style={{ color: "var(--pine)" }} /> Identity Verification</h3>
      <div className="mb-3">
        {uploaded.length > 0
          ? <span className="badge-pine" data-testid="id-status-uploaded">✓ ID Uploaded</span>
          : <span className="badge-terracotta" data-testid="id-status-missing">ID Missing</span>}
      </div>
      {docs.length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>No ID document on file.</p>}
      <div className="space-y-2">
        {docs.map((d) => (
          <div key={d.id} className="flex flex-wrap items-center gap-2 text-sm py-2 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`id-doc-${d.id}`}>
            <span className="font-medium">{d.id_type}</span>
            <span className="text-xs" style={{ color: "var(--muted)" }}>
              {d.guest_label || "Primary Guest"} · {d.uploaded_at ? new Date(d.uploaded_at).toLocaleString() : ""}
              {d.status === "DELETED" ? ` · DELETED${d.deleted_at ? ` ${new Date(d.deleted_at).toLocaleString()}` : ""} by ${d.deleted_by}` : ""}
            </span>
            {d.status === "UPLOADED" && (
              <>
                <button data-testid={`id-view-${d.id}`} onClick={() => viewDoc(d.id)} disabled={busyId === d.id} className="btn-outline inline-flex items-center gap-1" style={{ padding: "4px 10px", fontSize: 12 }}>
                  <Eye size={13} /> View ID
                </button>
                {canDelete && (
                  <button data-testid={`id-delete-${d.id}`} onClick={() => delDoc(d.id)} disabled={busyId === d.id} className="btn-outline inline-flex items-center gap-1" style={{ padding: "4px 10px", fontSize: 12, color: "var(--terracotta)" }}>
                    <Trash2 size={13} /> Delete
                  </button>
                )}
              </>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs mt-3" style={{ color: "var(--muted)" }}>Stored in private object storage. Every access is logged; visible to the Owner and the Responsible Manager of this stay only.</p>
    </div>
  );
}
